import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  REQUEST_FILTER_SECTION_EXPANSION_STORAGE_KEY,
  requestFilterCollapsibleSectionIds,
  requestFilterSectionDefaults,
} from './sectionExpansion';

const RequestFilterSectionExpansionContext = createContext(null);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const sanitizePreferences = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return requestFilterCollapsibleSectionIds.reduce((result, sectionId) => {
    if (typeof value[sectionId] === 'boolean') {
      result[sectionId] = value[sectionId];
    }

    return result;
  }, {});
};

const parsePreferences = (value) => {
  if (!value) return {};

  try {
    return sanitizePreferences(JSON.parse(value));
  } catch (error) {
    return {};
  }
};

const readPreferences = () => {
  if (typeof window === 'undefined') return {};

  try {
    return parsePreferences(
      window.localStorage.getItem(REQUEST_FILTER_SECTION_EXPANSION_STORAGE_KEY)
    );
  } catch (error) {
    return {};
  }
};

const writePreferences = (preferences) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      REQUEST_FILTER_SECTION_EXPANSION_STORAGE_KEY,
      JSON.stringify(preferences)
    );
  } catch (error) {}
};

export const RequestFilterSectionExpansionProvider = ({ children }) => {
  const [storedPreferences, setStoredPreferences] = useState(readPreferences);
  const [autoExpandedSections, setAutoExpandedSections] = useState({});

  const expandedSections = useMemo(
    () =>
      requestFilterCollapsibleSectionIds.reduce((result, sectionId) => {
        result[sectionId] = hasOwn(storedPreferences, sectionId)
          ? storedPreferences[sectionId]
          : autoExpandedSections[sectionId] ||
            requestFilterSectionDefaults[sectionId];

        return result;
      }, {}),
    [autoExpandedSections, storedPreferences]
  );

  const allSectionsExpanded = useMemo(
    () =>
      requestFilterCollapsibleSectionIds.every(
        (sectionId) => expandedSections[sectionId]
      ),
    [expandedSections]
  );

  const setSectionExpanded = useCallback((sectionId, expanded) => {
    if (!requestFilterCollapsibleSectionIds.includes(sectionId)) return;

    setStoredPreferences((currentPreferences) => ({
      ...currentPreferences,
      [sectionId]: expanded,
    }));
  }, []);

  const ensureSectionExpanded = useCallback((sectionId) => {
    if (!requestFilterCollapsibleSectionIds.includes(sectionId)) return;

    setAutoExpandedSections((currentSections) => {
      if (currentSections[sectionId]) return currentSections;

      return {
        ...currentSections,
        [sectionId]: true,
      };
    });
  }, []);

  const hasStoredPreference = useCallback(
    (sectionId) => hasOwn(storedPreferences, sectionId),
    [storedPreferences]
  );

  const toggleAllSections = useCallback(() => {
    const nextExpanded = !allSectionsExpanded;
    const nextPreferences = requestFilterCollapsibleSectionIds.reduce(
      (result, sectionId) => {
        result[sectionId] = nextExpanded;
        return result;
      },
      {}
    );

    setStoredPreferences(nextPreferences);
    setAutoExpandedSections({});
  }, [allSectionsExpanded]);

  useEffect(() => {
    writePreferences(storedPreferences);
  }, [storedPreferences]);

  const contextValue = useMemo(
    () => ({
      expandedSections,
      allSectionsExpanded,
      setSectionExpanded,
      ensureSectionExpanded,
      hasStoredPreference,
      toggleAllSections,
    }),
    [
      allSectionsExpanded,
      ensureSectionExpanded,
      expandedSections,
      hasStoredPreference,
      setSectionExpanded,
      toggleAllSections,
    ]
  );

  return (
    <RequestFilterSectionExpansionContext.Provider value={contextValue}>
      {children}
    </RequestFilterSectionExpansionContext.Provider>
  );
};

export const useRequestFilterSectionExpansion = () => {
  const context = useContext(RequestFilterSectionExpansionContext);

  if (!context) {
    throw new Error(
      'Request filter sections must be rendered inside their expansion provider'
    );
  }

  return context;
};
